import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid,
  FormControlLabel,
  Switch,
  TextField,
  Box,
  Button,
  ButtonGroup,
} from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Link } from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Underline from '@tiptap/extension-underline';
import { Editor, Node, Extension } from '@tiptap/core';
import { TextModuleSelector } from '../TextModule/TextModuleSelector';
import { DictionaryHelper } from '../Dictionary/DictionaryHelper';

const VariableNode = Node.create({
  name: 'variable',
  inline: true,
  group: 'inline',
  atom: true,
  addAttributes() {
    return { name: {} };
  },
  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },
  renderHTML({ node }) {
    return [
      'span',
      { 'data-variable': node.attrs.name, contenteditable: 'false' },
      `{{${node.attrs.name}}}`,
    ];
  },
});

const BackgroundColor = Extension.create({
  name: 'backgroundColor',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          backgroundColor: {
            default: null,
            parseHTML: element =>
              (element as HTMLElement).style.backgroundColor || null,
            renderHTML: attributes => {
              if (!attributes.backgroundColor) {
                return {};
              }

              return {
                style: `background-color: ${attributes.backgroundColor}`,
              };
            },
          },
        },
      },
    ];
  },
});

const TEMPLATE_VAR_REGEX = /{{\s*[^}]+\s*}}/g;
const WRAPPER_DISPLAY = 'block';
const WRAPPER_WIDTH = '100%';

type TokenMaps = {
  colorTokenToTemplate: Map<string, string>;
  backgroundTokenToTemplate: Map<string, string>;
  hrefTokenToTemplate: Map<string, string>;
};

const createEmptyTokenMaps = (): TokenMaps => ({
  colorTokenToTemplate: new Map<string, string>(),
  backgroundTokenToTemplate: new Map<string, string>(),
  hrefTokenToTemplate: new Map<string, string>(),
});

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseStyleDeclarations = (
  style: string
): Array<{ property: string; value: string }> =>
  style
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .map(declaration => {
      const colonIndex = declaration.indexOf(':');
      if (colonIndex === -1) return null;

      return {
        property: declaration.slice(0, colonIndex).trim().toLowerCase(),
        value: declaration.slice(colonIndex + 1).trim(),
      };
    })
    .filter((entry): entry is { property: string; value: string } => !!entry);

const buildStyleString = (
  declarations: Array<{ property: string; value: string }>
): string =>
  declarations.map(({ property, value }) => `${property}: ${value}`).join('; ');

const getStyleValue = (
  declarations: Array<{ property: string; value: string }>,
  propertyName: string
): string | null =>
  declarations.find(
    declaration => declaration.property === propertyName.toLowerCase()
  )?.value ?? null;

const removeStyleProperty = (
  declarations: Array<{ property: string; value: string }>,
  propertyName: string
): Array<{ property: string; value: string }> =>
  declarations.filter(
    declaration => declaration.property !== propertyName.toLowerCase()
  );

const setElementStyleDeclarations = (
  el: HTMLElement,
  declarations: Array<{ property: string; value: string }>
): void => {
  if (declarations.length === 0) {
    el.removeAttribute('style');
    return;
  }

  el.setAttribute('style', buildStyleString(declarations));
};

const wrapBackgroundColorBlocksForEditor = (doc: Document): void => {
  const elements = Array.from(
    doc.body.querySelectorAll<HTMLElement>('[style*="background-color"]')
  );

  elements.forEach(el => {
    const tagName = el.tagName.toLowerCase();

    if (tagName === 'span') {
      return;
    }

    const styleAttr = el.getAttribute('style');
    if (!styleAttr) {
      return;
    }

    const declarations = parseStyleDeclarations(styleAttr);
    const backgroundColor = getStyleValue(declarations, 'background-color');

    if (!backgroundColor) {
      return;
    }

    const remainingDeclarations = removeStyleProperty(
      declarations,
      'background-color'
    );
    setElementStyleDeclarations(el, remainingDeclarations);

    const wrapper = doc.createElement('span');
    wrapper.setAttribute(
      'style',
      buildStyleString([
        { property: 'background-color', value: backgroundColor },
        { property: 'display', value: WRAPPER_DISPLAY },
        { property: 'width', value: WRAPPER_WIDTH },
      ])
    );

    while (el.firstChild) {
      wrapper.appendChild(el.firstChild);
    }

    el.appendChild(wrapper);
  });
};

const unwrapBackgroundColorBlocksFromEditor = (doc: Document): void => {
  const candidateParents = Array.from(
    doc.body.querySelectorAll<HTMLElement>(
      'p, div, li, td, th, h1, h2, h3, h4, h5, h6'
    )
  );

  candidateParents.forEach(parent => {
    const children = Array.from(parent.childNodes);

    if (children.length !== 1) {
      return;
    }

    const onlyChild = children[0];
    if (
      !(onlyChild instanceof HTMLElement) ||
      onlyChild.tagName.toLowerCase() !== 'span'
    ) {
      return;
    }

    const wrapperStyleAttr = onlyChild.getAttribute('style');
    if (!wrapperStyleAttr) {
      return;
    }

    const wrapperDeclarations = parseStyleDeclarations(wrapperStyleAttr);
    const wrapperBackground = getStyleValue(
      wrapperDeclarations,
      'background-color'
    );
    const wrapperDisplay = getStyleValue(wrapperDeclarations, 'display');
    const wrapperWidth = getStyleValue(wrapperDeclarations, 'width');

    if (
      !wrapperBackground ||
      wrapperDisplay !== WRAPPER_DISPLAY ||
      wrapperWidth !== WRAPPER_WIDTH
    ) {
      return;
    }

    const parentDeclarations = parseStyleDeclarations(
      parent.getAttribute('style') || ''
    );
    const nextParentDeclarations = [
      ...removeStyleProperty(parentDeclarations, 'background-color'),
      { property: 'background-color', value: wrapperBackground },
    ];
    setElementStyleDeclarations(parent, nextParentDeclarations);

    while (onlyChild.firstChild) {
      parent.insertBefore(onlyChild.firstChild, onlyChild);
    }

    parent.removeChild(onlyChild);
  });
};

const normalizeRgbTokenPattern = (token: string): string => {
  const match = token.match(
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i
  );

  if (!match) {
    return escapeRegExp(token);
  }

  const [, r, g, b] = match;
  return `rgb\\(\\s*${r}\\s*,\\s*${g}\\s*,\\s*${b}\\s*\\)`;
};

const createProtectedHtml = (
  html: string
): { html: string; maps: TokenMaps } => {
  if (!html) {
    return { html: '', maps: createEmptyTokenMaps() };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const maps = createEmptyTokenMaps();

  wrapBackgroundColorBlocksForEditor(doc);

  let colorIndex = 0;
  let backgroundIndex = 0;
  let hrefIndex = 0;

  doc.body.querySelectorAll<HTMLElement>('*').forEach(el => {
    const style = el.getAttribute('style');

    if (style) {
      const parsedDeclarations = parseStyleDeclarations(style);
      const nextDeclarations: Array<{ property: string; value: string }> = [];

      parsedDeclarations.forEach(({ property, value }) => {
        if (property === 'color' && TEMPLATE_VAR_REGEX.test(value)) {
          const token = `rgb(10, 10, ${Math.min(180 + colorIndex, 250)})`;
          maps.colorTokenToTemplate.set(token, value);
          nextDeclarations.push({ property: 'color', value: token });
          colorIndex += 1;
          TEMPLATE_VAR_REGEX.lastIndex = 0;
          return;
        }

        if (property === 'background-color' && TEMPLATE_VAR_REGEX.test(value)) {
          const blue = Math.min(180 + backgroundIndex, 250);
          const token = `rgb(128, 128, ${blue})`;
          maps.backgroundTokenToTemplate.set(token, value);
          nextDeclarations.push({ property: 'background-color', value: token });
          backgroundIndex += 1;
          TEMPLATE_VAR_REGEX.lastIndex = 0;
          return;
        }

        TEMPLATE_VAR_REGEX.lastIndex = 0;
        nextDeclarations.push({ property, value });
      });

      setElementStyleDeclarations(el, nextDeclarations);
    }

    const href = el.getAttribute('href');
    if (href && TEMPLATE_VAR_REGEX.test(href)) {
      const token = `https://dino-placeholder-href-${hrefIndex}.invalid`;
      maps.hrefTokenToTemplate.set(token, href);
      el.setAttribute('href', token);
      hrefIndex += 1;
      TEMPLATE_VAR_REGEX.lastIndex = 0;
    } else {
      TEMPLATE_VAR_REGEX.lastIndex = 0;
    }
  });

  return {
    html: doc.body.innerHTML,
    maps,
  };
};

const restoreProtectedHtml = (html: string, maps: TokenMaps): string => {
  if (!html) return '';

  let restoredHtml = html;

  maps.colorTokenToTemplate.forEach((templateValue, token) => {
    const escapedToken = normalizeRgbTokenPattern(token);
    restoredHtml = restoredHtml.replace(
      new RegExp(`color:\\s*${escapedToken}`, 'g'),
      `color: ${templateValue}`
    );
  });

  maps.backgroundTokenToTemplate.forEach((templateValue, token) => {
    const escapedToken = normalizeRgbTokenPattern(token);
    restoredHtml = restoredHtml.replace(
      new RegExp(`background-color:\\s*${escapedToken}`, 'g'),
      `background-color: ${templateValue}`
    );
  });

  maps.hrefTokenToTemplate.forEach((templateValue, token) => {
    const escapedToken = escapeRegExp(token);
    restoredHtml = restoredHtml.replace(
      new RegExp(`href="${escapedToken}"`, 'g'),
      `href="${templateValue}"`
    );
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(restoredHtml, 'text/html');
  unwrapBackgroundColorBlocksFromEditor(doc);

  return doc.body.innerHTML;
};

interface IProps {
  txt: string;
  setTxt: (value: string) => void;
  idTextModuleCategory?: number;
  dictonaryIdsArray?: number[];
}

export const CustomEditor: React.FC<IProps> = ({
  txt,
  setTxt,
  idTextModuleCategory,
  dictonaryIdsArray,
}) => {
  const [htmlEditorOpen, setHtmlEditorOpen] = useState(false);
  const tokenMapsRef = useRef<TokenMaps>(createEmptyTokenMaps());

  const initialProtectedContent = useMemo(() => {
    const result = createProtectedHtml(txt || '');
    tokenMapsRef.current = result.maps;
    return result.html;
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      BackgroundColor,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      VariableNode,
      Placeholder.configure({ placeholder: 'Schreibe hier...' }),
    ],
    content: initialProtectedContent,
    onUpdate({ editor: currentEditor }: { editor: Editor }) {
      const rawHtml = currentEditor.getHTML();
      const restoredHtml = restoreProtectedHtml(rawHtml, tokenMapsRef.current);
      setTxt(restoredHtml);
    },
  });

  useEffect(() => {
    if (!editor || htmlEditorOpen) return;

    const currentHtml = restoreProtectedHtml(
      editor.getHTML(),
      tokenMapsRef.current
    );

    if (currentHtml !== txt) {
      const protectedResult = createProtectedHtml(txt || '');
      tokenMapsRef.current = protectedResult.maps;
      editor.commands.setContent(protectedResult.html, { emitUpdate: false });
    }
  }, [editor, txt, htmlEditorOpen]);

  const handleToggleView = () => {
    if (editor && htmlEditorOpen) {
      const protectedResult = createProtectedHtml(txt || '');
      tokenMapsRef.current = protectedResult.maps;
      editor.commands.setContent(protectedResult.html, { emitUpdate: false });
    }

    setHtmlEditorOpen(prev => !prev);
  };

  const textModuleWriteTxt = (value: string) => {
    setTxt(value);

    if (editor && !htmlEditorOpen) {
      const currentHtml = restoreProtectedHtml(
        editor.getHTML(),
        tokenMapsRef.current
      );

      if (currentHtml !== value) {
        const protectedResult = createProtectedHtml(value);
        tokenMapsRef.current = protectedResult.maps;
        editor.commands.setContent(protectedResult.html, {
          emitUpdate: false,
        });
      }
    }
  };

  const menuButtons = [
    { label: 'B', action: () => editor?.chain().focus().toggleBold().run() },
    { label: 'I', action: () => editor?.chain().focus().toggleItalic().run() },
    {
      label: 'U',
      action: () => editor?.chain().focus().toggleUnderline().run(),
    },
    {
      label: 'H1',
      action: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      label: 'H2',
      action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: 'H3',
      action: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      label: 'UL',
      action: () => editor?.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'OL',
      action: () => editor?.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Link',
      action: () => {
        const url = prompt('Link URL eingeben');
        if (url) {
          editor
            ?.chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
        }
      },
    },
    {
      label: 'Tabelle',
      action: () =>
        editor
          ?.chain()
          .focus()
          .insertTable({ rows: 2, cols: 2, withHeaderRow: true })
          .run(),
    },
  ];

  return (
    <>
      <Grid item xs={12} sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Switch checked={htmlEditorOpen} onChange={handleToggleView} />
          }
          label='HTML Ansicht'
          labelPlacement='start'
          sx={{ float: 'right' }}
        />

        <TextModuleSelector
          key='customeditor'
          floatRight
          idTextModuleCategory={idTextModuleCategory}
          txt={txt}
          setTxt={textModuleWriteTxt}
        />

        {dictonaryIdsArray && (
          <DictionaryHelper
            txt={txt}
            setTxt={textModuleWriteTxt}
            dictionaryIdsArray={dictonaryIdsArray}
            floatRight
          />
        )}
      </Grid>

      {!htmlEditorOpen && (
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <ButtonGroup variant='outlined' size='small'>
              {menuButtons.map(btn => (
                <Button key={btn.label} onClick={btn.action}>
                  {btn.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          <Box
            sx={{
              minHeight: 150,
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1,
              '& .ProseMirror': {
                minHeight: 150,
                outline: 'none',
              },
            }}
          >
            <EditorContent editor={editor} />
          </Box>
        </Grid>
      )}

      {htmlEditorOpen && (
        <Grid item xs={12}>
          <TextField
            label='HTML Editor'
            multiline
            fullWidth
            rows={10}
            value={txt}
            onChange={e => textModuleWriteTxt(e.target.value)}
          />
        </Grid>
      )}
    </>
  );
};
